import {
  ActionStore,
  createAction,
  createGetter,
  on,
  SOFTLINE_SERVICE_UUID,
  StoreFeature,
  SubscriptionStore,
  SyncedRemoteCollectionStore,
} from '@softline/core';
import {concat, lastValueFrom, tap, toArray} from 'rxjs';
import {map} from 'rxjs/operators';
import {ItemDetailService, LoadDetailParameters} from '../services/item-detail.service';
import {ItemDetail} from '../types/item-detail';
import {SOFTLINE_CONFIG_ARTIKEL_INFO_DETAIL_SEQUENCE_SIZE} from '../artikel-info.shared';

export type State = SyncedRemoteCollectionStore.State<ItemDetail>

const collectionStore = SyncedRemoteCollectionStore.create<ItemDetail>();

export interface LoadManyStocksParallelParams {
  ids: number[];
  attributes?: (keyof ItemDetail)[];
  vkforgRestriction: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace ItemDetailStore {

  export const mutations = {
    ...collectionStore.mutations
  };

  export const actions = {
    ...collectionStore.actions,
    loadManySequential: createAction<State, LoadManyStocksParallelParams, ItemDetail[]>('load many sequential'),
  };

  export const getters = {
    ...collectionStore.getters,
    total: createGetter<State, number>('item stocks total'),
  };

  export const feature: StoreFeature<State> = {
    initialState: {
      ...collectionStore.feature.initialState,
    },
    mutations: [
      ...collectionStore.feature.mutations
    ],
    actions: [
      ...collectionStore.feature.actions,
      on(actions.loadManySequential, async ({featureName, injector, commit, params}) => {
        const service = injector.get(ItemDetailService);
        const token = injector.get(SOFTLINE_SERVICE_UUID)();
        const sequenceSize = injector.get(SOFTLINE_CONFIG_ARTIKEL_INFO_DETAIL_SEQUENCE_SIZE);
        let requests: (LoadDetailParameters | any)[] = [];

        const ids = params.ids;
        const attributes: (keyof ItemDetail)[] = ['itemId', 'itemSupplier', 'id', 'info', 'itemStorage', 'informations', 'itemVkforg', 'symbols', 'itemUnitData', 'media']
        while (ids.length > 0) {
          const size = Math.min(ids.length, sequenceSize);
          requests = [
            ...requests,
            { ids: ids.splice(0, size), attributes: attributes, vkforgRestriction: params?.vkforgRestriction }
          ];
        }

        const sub$ = concat(
          ...requests.map(payload => service.load(payload))
        ).pipe(
          tap(o => commit(featureName, mutations.addOrUpdateMany, o)),
          toArray(),
          map((responses) => responses.map(o => o).reduce((a, b) => ([...a, ...b]))),
        );

        const subState$ = SubscriptionStore.handleSubscriptionState(sub$, featureName, commit, token);
        return await lastValueFrom(ActionStore.handleObservableActionState(
          subState$,
          featureName,
          commit,
          actions.loadManySequential.name,
          token
        ));
      }),
    ],
    getters: [
      ...collectionStore.feature.getters,
    ]
  };
}
